import React, { useState } from 'react';
import { Chip, Button } from '@heroui/react';
import { Envelope, ArrowRight, ArrowLeft, Paperclip, CheckCircle, ArrowBendUpLeft } from '@phosphor-icons/react';
import { HelpdeskEmail } from '../types';

interface EmailHistorySectionProps {
  emails: HelpdeskEmail[];
  onReplyToEmail?: (email: HelpdeskEmail) => void;
  replyingToEmail?: HelpdeskEmail | null;
}

export const EmailHistorySection: React.FC<EmailHistorySectionProps> = ({
  emails,
  onReplyToEmail,
  replyingToEmail
}) => {
  const [selectedEmail, setSelectedEmail] = useState<HelpdeskEmail | null>(null);

  // Sort emails by date (newest first)
  const sortedEmails = [...emails].sort(
    (a, b) => new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime()
  );

  const isRecentIncoming = (email: HelpdeskEmail) => {
    const emailDate = new Date(email.receivedDateTime);
    const now = new Date();
    const diffHours = (now.getTime() - emailDate.getTime()) / (1000 * 60 * 60);
    return email.direction === 'received' && diffHours <= 24;
  };

  if (emails.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Envelope className="h-16 w-16 text-neutral-secondary-mid/50 mb-4" />
        <p className="text-lg font-medium text-neutral-primary mb-2">Geen emails gevonden</p>
        <p className="text-sm text-neutral-secondary-mid">
          Er zijn nog geen emails gekoppeld aan dit ticket.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-full">
        {/* Email List */}
        <div className="bg-muted border border-muted-foreground/20 rounded-lg overflow-hidden flex flex-col">
          <div className="border-b border-neutral-primary/10 px-4 py-3 shrink-0">
            <div className="flex items-center gap-2">
              <Envelope size={18} className="text-moss-primary" />
              <h3 className="font-semibold text-neutral-primary text-sm">
                Email verloop ({emails.length})
              </h3>
            </div>
            <p className="text-xs text-neutral-secondary-mid mt-1">
              Klik op een email om details te bekijken
            </p>
          </div>
          <div className="overflow-y-auto flex-1 max-h-96">
            {sortedEmails.map((email) => {
              const isSent = email.direction === 'sent';
              const isRecent = isRecentIncoming(email);
              const isSelected = selectedEmail?.id === email.id;
              const isReplyingTo = replyingToEmail?.id === email.id;

              return (
                <div
                  key={email.id}
                  onClick={() => setSelectedEmail(email)}
                  className={`px-4 py-3 border-b border-neutral-primary/10 cursor-pointer hover:bg-neutral-primary/5 transition-colors ${
                    isSelected ? 'bg-moss-primary/10 border-l-4 border-l-moss-primary' : ''
                  } ${isReplyingTo ? 'bg-brick-primary/10 border-l-4 border-l-brick-primary' : ''}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {isSent ? (
                      <ArrowRight className="text-moss-primary shrink-0" size={14} />
                    ) : (
                      <ArrowLeft className="text-blue-600 shrink-0" size={14} />
                    )}
                    <Chip
                      size="sm"
                      className={`text-xs ${
                        isSent
                          ? 'bg-moss-primary/10 text-moss-primary'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {isSent ? 'Verzonden' : 'Ontvangen'}
                    </Chip>
                    <span className="text-xs text-neutral-secondary-mid">
                      {new Date(email.receivedDateTime).toLocaleDateString('nl-NL')}
                    </span>
                    {email.hasAttachments && (
                      <Paperclip size={12} className="text-neutral-secondary-mid" />
                    )}
                    {isReplyingTo && (
                      <Chip
                        size="sm"
                        className="bg-brick-primary text-white text-xs"
                        startContent={<ArrowBendUpLeft size={10} weight="bold" />}
                      >
                        Beantwoorden
                      </Chip>
                    )}
                  </div>
                  <div className="text-sm font-medium text-neutral-primary truncate">
                    {email.subject}
                  </div>
                  <div className="text-xs text-neutral-secondary-mid truncate">
                    Van: {email.from}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {!email.isRead && !isSent && (
                      <Chip size="sm" className="bg-red-100 text-red-700 text-xs">
                        Ongelezen
                      </Chip>
                    )}
                    {isRecent && (
                      <Chip size="sm" className="bg-orange-100 text-orange-700 text-xs">
                        Nieuw
                      </Chip>
                    )}
                    {email.noReplyNeeded && !isSent && (
                      <Chip
                        size="sm"
                        className="bg-moss-primary/10 text-moss-primary text-xs"
                        startContent={<CheckCircle size={10} weight="fill" />}
                      >
                        Geen antwoord nodig
                      </Chip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Email Detail */}
        <div className="bg-muted border border-muted-foreground/20 rounded-lg overflow-hidden flex flex-col">
          <div className="border-b border-neutral-primary/10 px-4 py-3 shrink-0 sticky top-0 z-10 bg-muted">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Envelope size={18} className="text-moss-primary" />
                <h3 className="font-semibold text-neutral-primary text-sm">Email Details</h3>
              </div>
              {selectedEmail && onReplyToEmail && (
                <Button
                  size="sm"
                  className={
                    replyingToEmail?.id === selectedEmail.id
                      ? 'bg-brick-primary text-white'
                      : 'bg-brick-secondary-fine text-brick-primary hover:bg-brick-primary hover:text-white'
                  }
                  startContent={<ArrowBendUpLeft size={14} weight="bold" />}
                  onPress={() => onReplyToEmail(selectedEmail)}
                >
                  {replyingToEmail?.id === selectedEmail.id ? 'Beantwoorden' : 'Reageer'}
                </Button>
              )}
            </div>
          </div>
          {selectedEmail ? (
            <div className="p-4 overflow-y-auto flex-1 max-h-96">
              {/* Email Header */}
              <div className="mb-4 pb-4 border-b border-neutral-primary/10">
                <div className="mb-2">
                  <h4 className="text-base font-semibold text-neutral-primary">
                    {selectedEmail.subject}
                  </h4>
                </div>
                <div className="text-sm text-neutral-secondary-mid space-y-1">
                  <div>
                    <strong>Van:</strong> {selectedEmail.from}
                  </div>
                  {selectedEmail.metadata?.to && (
                    <div>
                      <strong>Naar:</strong> {selectedEmail.metadata.to}
                    </div>
                  )}
                  <div>
                    <strong>Datum:</strong>{' '}
                    {new Date(selectedEmail.receivedDateTime).toLocaleString('nl-NL')}
                  </div>
                  <div className="flex items-center gap-2">
                    <strong>Richting:</strong>
                    <Chip
                      size="sm"
                      className={
                        selectedEmail.direction === 'sent'
                          ? 'bg-moss-primary/10 text-moss-primary'
                          : 'bg-blue-100 text-blue-700'
                      }
                    >
                      {selectedEmail.direction === 'sent' ? 'Verzonden' : 'Ontvangen'}
                    </Chip>
                  </div>
                </div>
              </div>

              {/* Email Body */}
              <div>
                <h5 className="font-medium text-neutral-primary mb-2 text-sm">Bericht</h5>
                <div
                  className="prose prose-sm max-w-none text-neutral-primary bg-white p-3 rounded-sm border text-sm"
                  dangerouslySetInnerHTML={{ __html: selectedEmail.body }}
                />
              </div>

              {/* Attachments indicator */}
              {selectedEmail.hasAttachments && (
                <div className="mt-4 pt-3 border-t border-neutral-primary/10">
                  <div className="flex items-center gap-2 text-sm text-neutral-secondary-mid">
                    <Paperclip size={14} />
                    <span>
                      {selectedEmail.attachments?.length || '?'} bijlage(n)
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 p-4 flex-1">
              <Envelope className="h-12 w-12 text-neutral-secondary-mid/50 mb-3" />
              <p className="text-sm text-neutral-secondary-mid text-center">
                Selecteer een email uit de lijst om details te bekijken
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
